import { Infra3DSDKError } from "../error/Infra3DSDKError";
import { EventEmitter } from "../utils/EventEmitter";
import { GeometryProviderBase } from "./GeometryProviderBase";
/**
 * @class DataProviderBase
 *
 * @classdesc Base class to extend if implementing a data provider
 * class.
 *
 * @fires IDataAddedEvent
 *
 * @example
 * ```
 * class MyDataProvider extends Mapillary.API.DataProviderBase {
 *      constructor() {
 *          super(new Mapillary.API.S2GeometryProvider());
 *      }
 *      ...
 * }
 * ```
 */
// TODO: Generic options !!!
export class DataProviderBase extends EventEmitter {
    /**
     * Create a new data provider base instance.
     *
     * @param {GeometryProviderBase} geometry - Geometry
     * provider instance.
     */
    constructor(_geometry) {
        super();
        this._geometry = _geometry;
        if (!(this._geometry instanceof GeometryProviderBase)) {
            throw new Infra3DSDKError("The data provider requires a geometry provider base instance.");
        }
    }
    /**
     * Get geometry property.
     *
     * @returns {GeometryProviderBase} Geometry provider instance.
     */
    get geometry() {
        return this._geometry;
    }
    /**
     * Get core properties for images in a geometry cell.
     *
     * @param {string} cellId - The id of the geometry cell.
     * @returns {Promise} Promise to the core nodes of the
     * requested cell id.
     * @throws {Error} Rejects the promise on errors.
     */
    getCoreImages(cellId) {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
    /**
     * Get fill properties for images.
     *
     * @param {Array<string>} imageKeys - The keys for the
     * images to retrieve.
     * @returns {Promise} Promise to the fill nodes of the
     * requested image keys.
     * @throws {Error} Rejects the promise on errors.
     */
    getFillImages(imageKeys) {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
    /**
     * Get all properties for images.
     *
     * @param {Array<string>} imageKeys - The keys for the
     * images to retrieve.
     * @returns {Promise} Promise to the full nodes of the
     * requested image keys.
     * @throws {Error} Rejects the promise on errors.
     */
    getFullImages(imageKeys) {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
    /**
     * Get sequences.
     *
     * @param {Array<string>} sequenceKeys - The keys for the
     * sequences to retrieve.
     * @returns {Promise} Promise to the sequences of the
     * requested image keys.
     * @throws {Error} Rejects the promise on errors.
     */
    getSequences(sequenceKeys) {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
    getFramesAsGeoJSON(extent, epsg, skip, limit, overlap) {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
    getRoutes(options) {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
    getRoutesOutline() {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
    touchEPSG() {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
    findNearest(easting, northing, epsg) {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
    getLidarFragments(options) {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
    getLidarFragmentLines(options) {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
    getLidarFragmentHexes(options) {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
    orderLidarFragments(email, user_uid, fragments_uids) {
        return Promise.reject(new Infra3DSDKError("Not implemented"));
    }
}
//# sourceMappingURL=DataProviderBase.js.map